import React, { useState, useEffect } from "react";
import {
  Phone,
  MessageCircle,
  ArrowRight,
  HeartPulse,
  Activity,
  Dumbbell,
  Sparkles,
  Stethoscope,
  Calendar,
  Video,
  ClipboardCheck,
  Star,
  Award,
  GraduationCap,
  Globe,
  Quote,
  Clock,
  Check,
  IndianRupee,
  QrCode,
  Copy,
  Shield,
} from "lucide-react";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { SITE } from "@/constants/testIds";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CallbackDialog from "@/components/CallbackDialog";

const ICONS = { HeartPulse, Activity, Dumbbell, Sparkles, Stethoscope };

const PILLAR_IMAGES = {
  "Age-Related Health":
    "https://images.pexels.com/photos/6975761/pexels-photo-6975761.jpeg",
  "Hormonal Balance":
    "https://images.unsplash.com/photo-1505751172876-fa1923c5c528?auto=format&fit=crop&w=1200&q=80",
  "Body Composition":
    "https://images.unsplash.com/photo-1747302653730-91070dfaa827",
  "Skin Health":
    "https://images.pexels.com/photos/10222453/pexels-photo-10222453.jpeg",
};

const PROCESS_STEPS = [
  {
    n: "01",
    icon: Calendar,
    title: "Book a slot",
    body: "Call +91 9319757979 or request a callback. Our team confirms a time that suits you.",
  },
  {
    n: "02",
    icon: ClipboardCheck,
    title: "Share your story",
    body: "A detailed intake — labs, lifestyle, symptoms — so we see the whole picture.",
  },
  {
    n: "03",
    icon: Video,
    title: "Virtual consult",
    body: "A 40–60 minute video consultation. Diagnosis, plan, and clarity in one session.",
  },
  {
    n: "04",
    icon: HeartPulse,
    title: "Ongoing care",
    body: "Reviews, course-corrections, and quiet support between consultations.",
  },
];

const FAQS = [
  {
    q: "Who is ReGain for?",
    a: "Adults — typically 35 to 65 — who want to age well, restore vitality, and address concerns across hormones, body composition and skin in one expert-led plan.",
  },
  {
    q: "What are your consultation hours?",
    a: "We see patients virtually Monday to Saturday, 10:00 AM to 7:00 PM IST. Book by calling +91 9319757979 or WhatsApp on +91 9871148090 — we'll confirm a slot that works for you.",
  },
  {
    q: "How long is a consultation and what does it cost?",
    a: "Two options: a focused 10-minute Quick Consult at ₹199 (best for follow-ups or a single question) and a thorough 20–25 minute Detailed Consult at ₹299 (best for first visits or complex cases). Both are delivered via secure video call.",
  },
  {
    q: "How do I pay?",
    a: "Payment is collected upfront via UPI — Google Pay, PhonePe, Paytm or any UPI app. You'll receive the UPI ID, QR code and clinic phone number once your slot is confirmed.",
  },
  {
    q: "Do I need lab tests before booking?",
    a: "Not necessarily. If you have recent labs, please share them. Otherwise, we'll recommend the right work-up after your first consultation.",
  },
  {
    q: "Is this medical or wellness coaching?",
    a: "It's medical care. ReGain is led by a qualified physician — treatments, prescriptions and protocols are evidence-based and personalised.",
  },
];

export default function Landing() {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [clinic, setClinic] = useState({ phone: "+919319757979", whatsapp: "+919871148090" });
  const [cbOpen, setCbOpen] = useState(false);

  // Intentional: fetch clinic data once on mount. `api` is a stable module singleton.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/public/clinic-info").then((r) => setClinic(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data || [])).catch(() => {});
  }, []);

  const phone = clinic.phone || "+919319757979";
  const whatsapp = clinic.whatsapp || "+919871148090";
  const whatsappDigits = whatsapp.replace(/[^0-9]/g, "");
  const whatsappLink = (text) =>
    `https://wa.me/${whatsappDigits}?text=${encodeURIComponent(text)}`;
  const phoneDisplay = phone.replace("+91", "+91 ");
  const openCb = () => setCbOpen(true);

  return (
    <div className="min-h-screen bg-cream text-ink">
      <Header phone={phone} whatsapp={whatsapp} onCallbackClick={openCb} />
      <CallbackDialog open={cbOpen} onOpenChange={setCbOpen} />

      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-line bg-surface/60 px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-ink-muted">
              <span className="w-1.5 h-1.5 rounded-full bg-sage animate-pulse" />
              Virtual clinic · India & abroad
            </div>
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-terracotta/10 text-terracotta border border-terracotta/30 px-4 py-1.5 text-xs uppercase tracking-[0.2em] font-medium">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
              Launch offer · 20% off your first consult
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mt-7 text-balance">
              Age, on{" "}
              <span className="italic text-sage">your</span> terms.
              <br className="hidden md:block" />
              <span className="text-ink-muted/80">A second wind for your body.</span>
            </h1>
            <p className="mt-7 text-lg text-ink-muted max-w-xl leading-relaxed">
              <span className="text-ink font-medium">ReGain</span> is a virtual
              clinic for age-related health, hormonal balance, body composition
              correction and skin wellness. Care that treats the whole picture,
              delivered from your living room.
            </p>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-10">
              <a
                href={`tel:${phone}`}
                data-testid={SITE.heroCallBtn}
                className="group inline-flex items-center justify-center gap-3 rounded-full bg-terracotta hover:bg-terracotta-dark text-cream px-7 py-4 text-base font-medium transition-all duration-300 hover:-translate-y-0.5 shadow-sm hover:shadow-md"
              >
                <Phone className="w-5 h-5" strokeWidth={1.75} />
                Call {phoneDisplay}
                <ArrowRight className="w-4 h-4 -ml-1 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a
                href={whatsappLink("Hi ReGain team, I'd like to know more about your consultations.")}
                target="_blank"
                rel="noreferrer"
                data-testid={SITE.heroWhatsappBtn}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-sage hover:bg-sage-dark text-cream px-7 py-4 text-base font-medium transition-all duration-300 hover:-translate-y-0.5"
              >
                <MessageCircle className="w-5 h-5" strokeWidth={1.75} />
                WhatsApp
              </a>
              <button
                data-testid={SITE.heroCallbackBtn}
                onClick={openCb}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-transparent text-ink border border-ink/15 hover:border-sage hover:text-sage px-7 py-4 text-base font-medium transition-all duration-300"
              >
                Request a callback
              </button>
            </div>

            <div className="grid grid-cols-3 gap-6 sm:gap-10 mt-14 max-w-lg">
              {[
                { k: "12+ yrs", v: "Clinical practice" },
                { k: "3,500+", v: "Lives transformed" },
                { k: "100%", v: "Virtual & private" },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-serif text-3xl text-ink leading-none">{s.k}</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-ink-muted mt-2">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative animate-fade-up [animation-delay:200ms]">
            <div className="relative rounded-[2.5rem] overflow-hidden border border-line bg-gradient-to-br from-surface via-cream to-surface/60 min-h-[560px] flex flex-col">
              <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
              <div className="relative flex-1 flex flex-col p-10 md:p-12">
                <div className="text-xs uppercase tracking-[0.3em] text-sage">
                  The ReGain promise
                </div>
                <div className="mt-8 font-serif text-4xl md:text-5xl lg:text-[3.4rem] leading-[1] tracking-tight text-ink">
                  Build muscles.
                  <br />
                  <span className="italic text-sage">Burn body fat.</span>
                </div>

                <div className="mt-10 flex items-center gap-3">
                  <span className="h-px flex-1 bg-line" />
                  <span className="text-[10px] uppercase tracking-[0.35em] text-ink-muted">
                    Our mantra
                  </span>
                  <span className="h-px flex-1 bg-line" />
                </div>

                <div className="mt-6 font-serif text-2xl md:text-[1.75rem] tracking-[0.06em] text-ink text-center">
                  <span className="text-ink-muted">Restart</span>
                  <span className="text-sage mx-3">·</span>
                  <span className="text-ink-muted">Restore</span>
                  <span className="text-sage mx-3">·</span>
                  <span className="italic text-sage font-medium">RéGain</span>
                </div>

                <div className="mt-auto pt-10 space-y-4">
                  {[
                    { k: "Hours", v: "Mon – Sat · 10 AM – 7 PM IST" },
                    { k: "Format", v: "Virtual · secure video call" },
                    { k: "From", v: "₹199 per session" },
                  ].map((row) => (
                    <div key={row.k} className="flex items-baseline gap-6 border-t border-line/70 pt-4">
                      <div className="text-[10px] uppercase tracking-[0.28em] text-ink-muted w-16 shrink-0">
                        {row.k}
                      </div>
                      <div className="text-sm text-ink flex-1">{row.v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:flex items-center gap-3 bg-cream border border-line rounded-2xl px-5 py-4 shadow-sm animate-float-slow">
              <div className="w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center">
                <Stethoscope className="w-5 h-5 text-sage" strokeWidth={1.5} />
              </div>
              <div className="leading-tight">
                <div className="text-xs uppercase tracking-[0.18em] text-ink-muted">Slots open</div>
                <div className="text-sm text-ink font-medium">This week</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="py-24 md:py-32 bg-surface/50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-sage">About</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1] tracking-tight mt-5 text-balance">
              Medicine for the
              <span className="italic text-sage"> long view.</span>
            </h2>
          </div>
          <div className="md:col-span-7 space-y-7 text-lg text-ink-muted leading-relaxed">
            <p>
              We don't believe ageing is something to fight. We believe it's
              something to <span className="text-ink">work with</span> — quietly,
              intelligently, year after year. ReGain exists for adults who want
              their next decades to feel as good as their best ones.
            </p>
            <p>
              ReGain brings together hormones, metabolism, body composition
              and skin into a single, coordinated plan. No silos. No
              conflicting advice. One playbook, one outcome.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6">
              {[
                { icon: GraduationCap, label: "Qualified physician" },
                { icon: Award, label: "Evidence-led care" },
                { icon: Globe, label: "Virtual, worldwide" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-start gap-3">
                  <Icon className="w-5 h-5 text-sage mt-0.5" strokeWidth={1.5} />
                  <span className="text-ink text-sm">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES / PILLARS */}
      <section id="services" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-sage">Care Pillars</div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mt-5 text-balance max-w-2xl">
                Four pillars,
                <span className="italic text-sage"> one</span> coherent plan.
              </h2>
            </div>
            <p className="text-ink-muted max-w-md text-base md:text-lg">
              Each area builds on the others. We work on all of them — or just
              what you need — guided by your goals and your labs.
            </p>
          </div>

          <div
            data-testid={SITE.servicesGrid}
            className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 auto-rows-[minmax(260px,auto)]"
          >
            {services.map((s, idx) => {
              const Icon = ICONS[s.icon] || Sparkles;
              const span =
                idx % 4 === 0 || idx % 4 === 3
                  ? "md:col-span-7"
                  : "md:col-span-5";
              const altSpan =
                idx % 4 === 0 || idx % 4 === 3
                  ? "md:col-span-5"
                  : "md:col-span-7";
              const finalSpan = idx % 2 === 0 ? span : altSpan;
              const img = PILLAR_IMAGES[s.title];
              return (
                <article
                  key={s.id}
                  data-testid={SITE.serviceCard(idx + 1)}
                  className={`relative group overflow-hidden rounded-3xl border border-line bg-surface ${finalSpan}`}
                >
                  {img && (
                    <img
                      src={img}
                      alt={s.title}
                      className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-75 transition-opacity duration-700"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/40 to-transparent" />
                  <div className="relative h-full p-8 md:p-10 flex flex-col justify-end min-h-[300px]">
                    <div className="w-11 h-11 rounded-full bg-cream/15 backdrop-blur-md border border-cream/20 flex items-center justify-center text-cream mb-5">
                      <Icon className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-cream/70">
                      {s.tagline}
                    </div>
                    <h3 className="font-serif text-3xl md:text-4xl text-cream tracking-tight mt-2">
                      {s.title}
                    </h3>
                    <p className="text-cream/85 mt-3 max-w-md leading-relaxed">
                      {s.description}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="py-24 md:py-32 bg-ink text-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.3em] text-sage-light">How it works</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mt-5 text-balance text-cream">
              Quiet, considered,
              <span className="italic text-sage-light"> entirely virtual.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px mt-16 bg-cream/10 border border-cream/10 rounded-3xl overflow-hidden">
            {PROCESS_STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.n}
                  data-testid={SITE.processStep(i + 1)}
                  className="bg-ink p-8 md:p-10 group hover:bg-ink/60 transition-colors duration-500"
                >
                  <div className="flex items-center justify-between">
                    <Icon className="w-7 h-7 text-sage-light" strokeWidth={1.4} />
                    <span className="font-serif text-3xl text-cream/30">{step.n}</span>
                  </div>
                  <h3 className="font-serif text-2xl text-cream mt-8">{step.title}</h3>
                  <p className="text-cream/65 mt-3 text-sm leading-relaxed">{step.body}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-4">
            <a
              href={`tel:${phone}`}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-dark text-cream px-7 py-4 text-base font-medium transition-all duration-300 hover:-translate-y-0.5"
            >
              <Phone className="w-5 h-5" strokeWidth={1.75} /> Call {phoneDisplay}
            </a>
            <a
              href={whatsappLink("Hi ReGain team, I'd like to book a consultation.")}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-sage hover:bg-sage-dark text-cream px-7 py-4 text-base font-medium transition-all duration-300"
            >
              <MessageCircle className="w-5 h-5" strokeWidth={1.75} /> WhatsApp the clinic
            </a>
            <button
              onClick={openCb}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-cream/20 hover:border-cream text-cream px-7 py-4 text-base font-medium transition-all duration-300"
            >
              Request a callback
            </button>
          </div>
        </div>
      </section>

      {/* PRICING & PAYMENT */}
      <section id="pricing" className="py-24 md:py-32 bg-surface/50">
        <div className="max-w-7xl mx-auto px-6 md:px-10" data-testid={SITE.pricingSection}>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-sage">Consultation & Pricing</div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mt-5 text-balance max-w-2xl">
                Honest pricing.
                <br />
                <span className="italic text-sage">No surprises.</span>
              </h2>
            </div>
            <div className="flex items-center gap-3 text-ink-muted">
              <Clock className="w-5 h-5 text-sage" strokeWidth={1.5} />
              <div className="text-sm">
                <div className="text-ink font-medium">Mon – Sat · 10 AM to 7 PM IST</div>
                <div className="text-xs uppercase tracking-[0.2em] mt-0.5">Booking by phone or WhatsApp</div>
              </div>
            </div>
          </div>

          {/* Pricing cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {(clinic.consultations || []).map((c) => {
              const isPopular = c.popular;
              return (
                <article
                  key={c.key}
                  data-testid={SITE.pricingCard(c.key)}
                  className={`relative rounded-3xl p-8 md:p-10 transition-all duration-300 hover:-translate-y-1 ${
                    isPopular
                      ? "bg-ink text-cream border border-ink"
                      : "bg-cream border border-line"
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-8 inline-flex items-center gap-1.5 bg-terracotta text-cream text-[10px] uppercase tracking-[0.25em] font-medium px-3 py-1.5 rounded-full">
                      <Sparkles className="w-3 h-3" strokeWidth={2} /> Most chosen
                    </div>
                  )}
                  <div className={`text-xs uppercase tracking-[0.2em] ${isPopular ? "text-sage-light" : "text-sage"}`}>
                    {c.duration_minutes}-{c.duration_minutes === 20 ? "25" : c.duration_minutes} min · Video
                  </div>
                  <h3 className={`font-serif text-3xl md:text-4xl tracking-tight mt-3 ${isPopular ? "text-cream" : "text-ink"}`}>
                    {c.title}
                  </h3>
                  <p className={`mt-4 text-base leading-relaxed ${isPopular ? "text-cream/75" : "text-ink-muted"}`}>
                    {c.description}
                  </p>
                  <div className="flex items-baseline gap-2 mt-7">
                    <IndianRupee className={`w-6 h-6 ${isPopular ? "text-cream" : "text-ink"}`} strokeWidth={1.75} />
                    <span className={`font-serif text-5xl md:text-6xl tracking-tight ${isPopular ? "text-cream" : "text-ink"}`}>
                      {c.price_inr}
                    </span>
                    {c.original_price_inr && c.original_price_inr > c.price_inr && (
                      <span
                        className={`font-serif text-2xl md:text-3xl line-through tracking-tight ml-2 ${
                          isPopular ? "text-cream/40" : "text-ink-muted/60"
                        }`}
                      >
                        ₹{c.original_price_inr}
                      </span>
                    )}
                    <span className={`text-sm uppercase tracking-[0.2em] ml-1 ${isPopular ? "text-cream/60" : "text-ink-muted"}`}>
                      per session
                    </span>
                  </div>
                  {c.original_price_inr && c.original_price_inr > c.price_inr && (
                    <div
                      className={`inline-flex items-center gap-1.5 mt-3 text-[11px] uppercase tracking-[0.22em] font-medium px-2.5 py-1 rounded-full ${
                        isPopular
                          ? "bg-terracotta/20 text-terracotta border border-terracotta/30"
                          : "bg-terracotta/10 text-terracotta border border-terracotta/20"
                      }`}
                    >
                      <Sparkles className="w-3 h-3" strokeWidth={2} />
                      Launch price · save ₹{c.original_price_inr - c.price_inr}
                    </div>
                  )}
                  <ul className="space-y-3 mt-8">
                    {c.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm">
                        <Check
                          className={`w-4 h-4 mt-0.5 shrink-0 ${isPopular ? "text-sage-light" : "text-sage"}`}
                          strokeWidth={2}
                        />
                        <span className={isPopular ? "text-cream/85" : "text-ink"}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-col sm:flex-row gap-3 mt-10 pt-8 border-t border-current/10">
                    <a
                      href={`tel:${phone}`}
                      data-testid={SITE.pricingBookBtn(c.key)}
                      className={`flex-1 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium transition-all duration-300 ${
                        isPopular
                          ? "bg-terracotta hover:bg-terracotta-dark text-cream"
                          : "bg-ink hover:bg-sage-dark text-cream"
                      }`}
                    >
                      <Phone className="w-4 h-4" strokeWidth={1.75} /> Book by call
                    </a>
                    <a
                      href={whatsappLink(`Hi ReGain team, I'd like to book a ${c.title} (₹${c.price_inr}).`)}
                      target="_blank"
                      rel="noreferrer"
                      className={`flex-1 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium transition-all duration-300 border ${
                        isPopular
                          ? "border-cream/30 text-cream hover:bg-cream hover:text-ink"
                          : "border-sage/40 text-sage hover:bg-sage hover:text-cream"
                      }`}
                    >
                      <MessageCircle className="w-4 h-4" strokeWidth={1.75} /> WhatsApp
                    </a>
                  </div>
                </article>
              );
            })}
          </div>

          {/* Payment box */}
          <div className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center bg-cream border border-line rounded-3xl p-8 md:p-12">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.3em] text-sage">Payment</div>
              <h3 className="font-serif text-3xl md:text-4xl tracking-tight mt-4 text-balance">
                Pay in seconds via{" "}
                <span className="italic text-sage">any UPI app.</span>
              </h3>
              <p className="text-ink-muted mt-4 leading-relaxed max-w-lg">
                We collect the consultation fee upfront via UPI to confirm your
                slot. Google Pay · PhonePe · Paytm · BHIM — whichever you use,
                payment lands instantly.
              </p>

              <div className="space-y-3 mt-8">
                <div className="flex items-center gap-3 bg-surface border border-line rounded-2xl px-5 py-4">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-ink-muted w-20">UPI ID</div>
                  <div
                    className="font-mono text-base text-ink flex-1"
                    data-testid={SITE.paymentUpiId}
                  >
                    {clinic.payment?.upi_id || "jeevansamyo@okhdfcbank"}
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(clinic.payment?.upi_id || "jeevansamyo@okhdfcbank");
                      toast.success("UPI ID copied");
                    }}
                    data-testid={SITE.paymentUpiCopy}
                    className="text-sage hover:text-sage-dark text-xs uppercase tracking-[0.2em] inline-flex items-center gap-1.5 transition-colors"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy
                  </button>
                </div>
                <div className="flex items-center gap-3 bg-surface border border-line rounded-2xl px-5 py-4">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-ink-muted w-20">UPI Phone</div>
                  <div
                    className="font-mono text-base text-ink flex-1"
                    data-testid={SITE.paymentUpiPhone}
                  >
                    {clinic.payment?.upi_phone || whatsapp}
                  </div>
                  <a
                    href={`tel:${clinic.payment?.upi_phone || whatsapp}`}
                    className="text-sage hover:text-sage-dark text-xs uppercase tracking-[0.2em] inline-flex items-center gap-1.5 transition-colors"
                  >
                    <Phone className="w-3.5 h-3.5" /> Call
                  </a>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-7">
                {(clinic.payment?.methods || ["UPI", "Google Pay", "PhonePe", "Paytm"]).map((m) => (
                  <span
                    key={m}
                    className="text-[11px] uppercase tracking-[0.18em] text-ink-muted border border-line bg-surface/60 rounded-full px-3 py-1.5"
                  >
                    {m}
                  </span>
                ))}
              </div>

              <p className="flex items-start gap-2 text-xs text-ink-muted mt-7 max-w-lg leading-relaxed">
                <Shield className="w-4 h-4 mt-0.5 text-sage shrink-0" strokeWidth={1.5} />
                Receipts shared on WhatsApp · Refundable if slot is cancelled
                with 4+ hours notice.
              </p>
            </div>

            <div className="lg:col-span-5">
              <div
                data-testid={SITE.paymentQrPlaceholder}
                className="relative aspect-square max-w-[340px] mx-auto rounded-3xl bg-cream border border-line p-4 shadow-sm"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_skin-health-consult/artifacts/ntsehosp_IMG-20260603-WA0000.jpg"
                  alt={`UPI QR code · ${clinic.payment?.upi_id || "jeevansamyo@okhdfcbank"}`}
                  className="w-full h-full object-contain rounded-2xl"
                />
              </div>
              <div className="text-center mt-4 text-xs uppercase tracking-[0.2em] text-ink-muted">
                Scan to pay with any UPI app
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HEALTH PACKAGES */}
      <section id="packages" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10" data-testid={SITE.packagesSection}>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-sage">Health Packages</div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mt-5 text-balance max-w-2xl">
                Ongoing care,
                <br />
                <span className="italic text-sage">designed around you.</span>
              </h2>
            </div>
            <p className="text-ink-muted max-w-md text-base md:text-lg">
              Pick a duration that matches your goals. Every package is
              personalised — pricing depends on your needs.
            </p>
          </div>

          {/* Tier cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[
              {
                key: "monthly",
                tier: "01",
                title: "Monthly",
                period: "1 month",
                blurb: "Get started. Targeted help for a single concern, with one full consult and a follow-up review.",
              },
              {
                key: "quarterly",
                tier: "02",
                title: "Quarterly",
                period: "3 months",
                blurb: "Build momentum. Two reviews, weekly chat support, and tracked progress across labs and lifestyle.",
              },
              {
                key: "biannual",
                tier: "03",
                title: "Bi-annual",
                period: "6 months",
                blurb: "Real transformation. Four reviews, comprehensive labs, and a re-engineered plan as you progress.",
                popular: true,
              },
              {
                key: "annual",
                tier: "04",
                title: "Annual",
                period: "12 months",
                blurb: "Best value. Continuous physician oversight, quarterly deep dives, and full chronic-care management.",
              },
            ].map((p) => (
              <article
                key={p.key}
                data-testid={SITE.packageCard(p.key)}
                className={`relative rounded-3xl p-7 md:p-8 transition-all duration-300 hover:-translate-y-1 ${
                  p.popular
                    ? "bg-sage text-cream border border-sage"
                    : "bg-surface border border-line"
                }`}
              >
                {p.popular && (
                  <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 bg-terracotta text-cream text-[10px] uppercase tracking-[0.25em] font-medium px-3 py-1.5 rounded-full">
                    <Sparkles className="w-3 h-3" strokeWidth={2} /> Recommended
                  </div>
                )}
                <div className={`font-serif text-3xl ${p.popular ? "text-cream/40" : "text-ink-muted/40"}`}>
                  {p.tier}
                </div>
                <h3 className={`font-serif text-2xl md:text-3xl tracking-tight mt-6 ${p.popular ? "text-cream" : "text-ink"}`}>
                  {p.title}
                </h3>
                <div className={`text-xs uppercase tracking-[0.2em] mt-2 ${p.popular ? "text-cream/70" : "text-sage"}`}>
                  {p.period}
                </div>
                <p className={`text-sm leading-relaxed mt-5 ${p.popular ? "text-cream/85" : "text-ink-muted"}`}>
                  {p.blurb}
                </p>
                <div className={`mt-7 pt-5 border-t flex items-center justify-between text-xs uppercase tracking-[0.18em] ${
                  p.popular ? "border-cream/15 text-cream/70" : "border-line text-ink-muted"
                }`}>
                  <span>Pricing</span>
                  <span className={p.popular ? "text-cream" : "text-ink"}>On call</span>
                </div>
              </article>
            ))}
          </div>

          {/* Customisation callout */}
          <div className="mt-14 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start bg-surface border border-line rounded-3xl p-8 md:p-12">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-cream border border-line px-4 py-1.5 text-xs uppercase tracking-[0.22em] text-sage font-medium">
                <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
                Personalised
              </div>
              <h3 className="font-serif text-3xl md:text-4xl tracking-tight mt-5 text-balance">
                Every package is{" "}
                <span className="italic text-sage">tailored to your body.</span>
              </h3>
              <p className="text-ink-muted mt-5 leading-relaxed text-base md:text-lg max-w-xl">
                Packages are personalised to match your goals, history
                and comorbidities. The plan, frequency and protocols change
                depending on factors like:
              </p>
              <div className="flex flex-wrap gap-2 mt-6">
                {[
                  "Excess body weight",
                  "Diabetes",
                  "High blood pressure",
                  "Heart disease",
                  "Joint disease",
                  "Hormonal imbalance",
                  "Skin conditions",
                ].map((c) => (
                  <span
                    key={c}
                    className="text-xs uppercase tracking-[0.16em] bg-cream border border-line text-ink-muted rounded-full px-3.5 py-1.5"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-line">
              <div className="text-xs uppercase tracking-[0.2em] text-ink-muted">
                Ready to start?
              </div>
              <p className="font-serif text-2xl md:text-3xl text-ink mt-3 leading-snug">
                Speak to our team to pick the right package for you.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-7">
                <a
                  href={`tel:${phone}`}
                  data-testid={SITE.packagesCallBtn}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-dark text-cream px-5 py-3.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5"
                >
                  <Phone className="w-4 h-4" strokeWidth={1.75} /> Call to enquire
                </a>
                <a
                  href={whatsappLink("Hi ReGain team, I'd like to know more about your health packages.")}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={SITE.packagesWhatsappBtn}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-sage hover:bg-sage-dark text-cream px-5 py-3.5 text-sm font-medium transition-all duration-300"
                >
                  <MessageCircle className="w-4 h-4" strokeWidth={1.75} /> WhatsApp
                </a>
              </div>
              <p className="text-xs text-ink-muted mt-5 leading-relaxed">
                Free 5-minute discovery call to help you pick the right
                duration. No commitment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-sage">Stories</div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mt-5 max-w-2xl text-balance">
                A quieter strength,
                <br />
                <span className="italic text-sage">in their own words.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {testimonials.map((t, i) => (
              <figure
                key={t.id}
                data-testid={SITE.testimonialCard(i + 1)}
                className={`relative rounded-3xl bg-surface border border-line p-8 md:p-10 ${
                  i === 1 ? "md:translate-y-8" : ""
                }`}
              >
                <Quote className="w-7 h-7 text-sage/40 mb-5" strokeWidth={1.5} />
                <blockquote className="font-serif text-xl md:text-2xl text-ink leading-snug">
                  “{t.quote}”
                </blockquote>
                <figcaption className="flex items-center gap-4 mt-8 pt-6 border-t border-line">
                  {t.image_url && (
                    <img
                      src={t.image_url}
                      alt={t.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  )}
                  <div>
                    <div className="text-ink font-medium">{t.name}</div>
                    <div className="text-xs text-ink-muted uppercase tracking-[0.18em] mt-0.5">
                      {[t.age ? `${t.age}` : null, t.location].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div className="ml-auto flex gap-0.5" aria-label="5 star rating">
                    {["s1", "s2", "s3", "s4", "s5"].map((sid) => (
                      <Star key={sid} className="w-3.5 h-3.5 fill-terracotta text-terracotta" />
                    ))}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 md:py-32 bg-surface/50">
        <div className="max-w-5xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.3em] text-sage">FAQ</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-5 text-balance">
              Honest answers
              <br />
              <span className="italic text-sage">to fair questions.</span>
            </h2>
          </div>
          <div className="md:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`item-${i}`}
                  data-testid={SITE.faqItem(i + 1)}
                  className="border-b border-line"
                >
                  <AccordionTrigger className="text-left font-serif text-xl md:text-2xl text-ink hover:no-underline py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-ink-muted text-base leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-balance">
            Your next decade
            <br />
            <span className="italic text-sage">starts with one call.</span>
          </h2>
          <p className="text-ink-muted mt-7 text-lg max-w-xl mx-auto leading-relaxed">
            Pick up the phone, or leave us your number. We'll handle the rest.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
            <a
              href={`tel:${phone}`}
              className="inline-flex items-center justify-center gap-3 rounded-full bg-terracotta hover:bg-terracotta-dark text-cream px-8 py-4 text-base font-medium transition-all duration-300 hover:-translate-y-0.5"
            >
              <Phone className="w-5 h-5" strokeWidth={1.75} /> Call {phoneDisplay}
            </a>
            <a
              href={whatsappLink("Hi ReGain team, I'd like to start with ReGain.")}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-sage hover:bg-sage-dark text-cream px-8 py-4 text-base font-medium transition-all duration-300 hover:-translate-y-0.5"
            >
              <MessageCircle className="w-5 h-5" strokeWidth={1.75} /> WhatsApp
            </a>
            <button
              onClick={openCb}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-ink/15 hover:border-sage hover:text-sage text-ink px-8 py-4 text-base font-medium transition-all duration-300"
            >
              Request a callback
            </button>
          </div>
        </div>
      </section>

      <Footer phone={phone} whatsapp={whatsapp} onCallbackClick={openCb} />

      {/* Sticky mobile call + whatsapp */}
      <div className="md:hidden fixed bottom-5 right-5 z-30 flex flex-col items-end gap-3">
        <a
          href={whatsappLink("Hi ReGain team, I'd like a consultation.")}
          target="_blank"
          rel="noreferrer"
          data-testid={SITE.stickyWhatsappBtn}
          className="inline-flex items-center gap-2 rounded-full bg-sage text-cream px-5 py-3.5 shadow-lg shadow-ink/20 active:scale-95 transition-transform"
          aria-label="WhatsApp the doctor"
        >
          <MessageCircle className="w-5 h-5" strokeWidth={1.75} />
          <span className="text-sm font-medium">WhatsApp</span>
        </a>
        <a
          href={`tel:${phone}`}
          data-testid={SITE.stickyCallBtn}
          className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-5 py-3.5 shadow-lg shadow-ink/20 active:scale-95 transition-transform"
        >
          <Phone className="w-5 h-5" strokeWidth={1.75} />
          <span className="text-sm font-medium">Call Now</span>
        </a>
      </div>
    </div>
  );
}
